import { createEffect, createEvent, createStore, sample } from 'effector';
import { $currentObject, $currentObjectId, appApi } from '@buildex/core';
import type { LaunchStep } from '@buildex/module-contract';

/**
 * Статус шагов запуска объекта («дорожка» на Главной).
 * Пока у App API нет GET /objects со статусом, считаем его на клиенте
 * из шахматки и конфига объекта (см. docs/backend/user-flow-api.md, §2).
 */

export interface LaunchStatus {
  objectId: string;
  /** Помещений в шахматке; 0 — шахматки ещё нет */
  unitsCount: number;
  /** Помещения в продаже (не sold / withdrawn) */
  sellableCount: number;
  /** Из них с ценой */
  pricedCount: number;
  /** Конфиг ДЦО существует (GET /config ≠ 404) */
  dcoConfigured: boolean;
}

export type StepState = 'done' | 'partial' | 'pending';

export function stepState(status: LaunchStatus, step: LaunchStep): StepState {
  switch (step) {
    case 1:
      return status.unitsCount > 0 ? 'done' : 'pending';
    case 2:
      if (status.sellableCount > 0 && status.pricedCount === status.sellableCount) return 'done';
      return status.pricedCount > 0 ? 'partial' : 'pending';
    case 3:
      return status.dcoConfigured ? 'done' : 'pending';
  }
}

/** Шаг доступен, когда предыдущий пройден (первый — всегда) */
export function stepAvailable(status: LaunchStatus, step: LaunchStep): boolean {
  if (step === 1) return true;
  return stepState(status, (step - 1) as LaunchStep) === 'done';
}

/** Первый непройденный шаг; null — объект запущен полностью */
export function nextStep(status: LaunchStatus): LaunchStep | null {
  for (const step of [1, 2, 3] as const) {
    if (stepState(status, step) !== 'done') return step;
  }
  return null;
}

export const launchStatusRequested = createEvent<void>();

export const fetchLaunchStatusFx = createEffect<string, LaunchStatus, Error>(async (objectId) => {
  const [chessboard, config] = await Promise.all([
    appApi.getChessboard(objectId),
    appApi.getObjectConfig(objectId),
  ]);
  const sellable = chessboard.units.filter((u) => u.status !== 'sold' && u.status !== 'withdrawn');
  return {
    objectId,
    unitsCount: chessboard.units.length,
    sellableCount: sellable.length,
    pricedCount: sellable.filter((u) => u.price !== null).length,
    dcoConfigured: config !== null,
  };
});

export const $launchStatus = createStore<LaunchStatus | null>(null)
  .on(fetchLaunchStatusFx.doneData, (_, status) => status)
  .reset($currentObject.updates);

export const $launchStatusError = createStore<string | null>(null)
  .on(fetchLaunchStatusFx.failData, (_, error) => error.message)
  .reset(fetchLaunchStatusFx, $currentObject.updates);

export const $launchStatusLoading = fetchLaunchStatusFx.pending;

sample({
  clock: [launchStatusRequested, $currentObjectId.updates],
  source: $currentObjectId,
  filter: (id): id is string => id !== null,
  target: fetchLaunchStatusFx,
});
